import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Encounter, Observation, QuestionnaireResponse } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Alert, Button, Group, Stack } from "@mantine/core";

import { format } from "date-fns";

import { Loading } from "@/components/Loading";
import { TextWrapper } from "@/components/TextWrapper";
import { EncounterDifference } from "@/encounters/components/AmendEncounterDetails/components/EncounterDifference";
import { EncounterNoteDifference } from "@/encounters/components/AmendEncounterDetails/components/EncounterNoteDifference";
import { EncounterSummaryDifference } from "@/encounters/components/AmendEncounterDetails/components/EncounterSummaryDifference";
import {
  fetchLaborStageObservations,
  fetchLaborStageObservationsWithMembersForVersionDiff,
} from "@/encounters/components/EncounterLabor/services/labor";
import { fetchEncounterFlowsheetQuestionnaireResponsesById } from "@/encounters/components/EncounterLabor/utils/fetch-encounter-flowsheet-questionnaire-responses";
import { resolveLaborStageMembershipChangesForVersionDiff } from "@/encounters/components/EncounterLabor/utils/resolve-labor-stage-membership-changes-for-version-diff";
import ProceduresAmendmentDiff from "@/encounters/components/ProceduresAmendmentDiff";
import { FilesDiff } from "@/encounters/components/shared/Files";
import { fetchEncounterQuestionnaireResponseVersionPairForDiff } from "@/encounters/services/questionnaire-response";
import type { LaborAmendStageMembershipChangeRecord } from "@/encounters/types/labor-amend-provenance";
import {
  fetchLaborAmendCompletionProvenanceForVersion,
  loadLaborAmendProvenanceViewModel,
} from "@/encounters/utils/fetch-labor-amend-provenance-view";
import { LOINCAnswerCodes } from "@/fhir/enums/loinc";
import NotFound from "@/NotFound";
import { toast } from "@/utils/notifications";
import { captureExceptionWithoutPhi } from "@/utils/sentry";

import DxDiagnosesVersionDiff from "../../DxPlan/DxDiagnosesVersionDiff";

import LaborStageDiff from "./LaborStageDiff";
import LaborVersionDiffHeader from "./LaborVersionDiffHeader";
import PostpartumDiff from "./PostpartumDiff";

const LaborVersionDiff = () => {
  const { encounterId, versionId, clientId } = useParams<{
    encounterId: string;
    versionId: string;
    clientId: string;
  }>();

  const [encounter, setEncounter] = useState<Encounter>();
  const [currentVersion, setCurrentVersion] = useState<QuestionnaireResponse>();
  const [previousVersion, setPreviousVersion] = useState<QuestionnaireResponse>();
  const [laborStageObservations, setLaborStageObservations] = useState<Observation[]>([]);
  const [laborStageObservationsForDiff, setLaborStageObservationsForDiff] = useState<Observation[]>(
    [],
  );
  const [questionnaireResponsesById, setQuestionnaireResponsesById] = useState<
    Map<string, QuestionnaireResponse>
  >(new Map());
  const [provenanceTargetObservationIds, setProvenanceTargetObservationIds] = useState<
    Set<string> | undefined
  >(undefined);
  const [
    provenanceTargetFlowsheetQuestionnaireResponseIds,
    setProvenanceTargetFlowsheetQuestionnaireResponseIds,
  ] = useState<Set<string> | undefined>(undefined);
  const [stageMembershipChanges, setStageMembershipChanges] = useState<
    LaborAmendStageMembershipChangeRecord[]
  >([]);
  const [isFetchingInitialData, setIsFetchingInitialData] = useState(true);
  const navigate = useNavigate();
  const medplum = useMedplum();

  const provenanceObservationsById = useMemo(() => {
    const observationsById = new Map<string, Observation>();
    for (const observation of laborStageObservationsForDiff) {
      if (observation.id) {
        observationsById.set(observation.id, observation);
      }
    }
    return observationsById;
  }, [laborStageObservationsForDiff]);

  const stageCodesWithMembershipChanges = useMemo(
    () =>
      new Set(
        stageMembershipChanges.flatMap((change) =>
          [change.fromStageLoincCode, change.toStageLoincCode].filter(
            (stageCode): stageCode is string => Boolean(stageCode),
          ),
        ),
      ),
    [stageMembershipChanges],
  );

  useEffect(() => {
    const getVersionHistory = async () => {
      medplum.invalidateSearches("QuestionnaireResponse");
      medplum.invalidateSearches("Observation");
      medplum.invalidateSearches("Provenance");

      setIsFetchingInitialData(true);
      setCurrentVersion(undefined);
      setPreviousVersion(undefined);
      setLaborStageObservations([]);
      setLaborStageObservationsForDiff([]);
      setQuestionnaireResponsesById(new Map());
      setProvenanceTargetObservationIds(undefined);
      setProvenanceTargetFlowsheetQuestionnaireResponseIds(undefined);
      setStageMembershipChanges([]);

      try {
        if (!versionId || !encounterId || !clientId) {
          return;
        }

        const encounterResource = await medplum.readResource("Encounter", encounterId);
        if (!encounterResource) return;

        setEncounter(encounterResource);

        const versionPair = await fetchEncounterQuestionnaireResponseVersionPairForDiff(
          medplum,
          encounterResource,
          versionId,
        );

        let resolvedCurrentVersion = versionPair?.currentVersion;
        const resolvedPreviousVersion = versionPair?.previousVersion;
        const encounterQuestionnaireResponseId = versionPair?.currentVersion?.id;

        const amendProvenance = await fetchLaborAmendCompletionProvenanceForVersion(
          medplum,
          encounterId,
          versionId,
          encounterQuestionnaireResponseId,
        );

        let resolvedQuestionnaireResponsesById =
          await fetchEncounterFlowsheetQuestionnaireResponsesById(medplum, clientId, encounterId);

        let provenanceStageMembershipChanges: LaborAmendStageMembershipChangeRecord[] = [];
        let provenanceObservations: Observation[] = [];
        let provenanceTargetObservationIdsForDiff: Set<string> | undefined;

        if (amendProvenance) {
          const provenanceView = await loadLaborAmendProvenanceViewModel(medplum, amendProvenance);
          if (provenanceView.encounterQuestionnaireResponse) {
            resolvedCurrentVersion = provenanceView.encounterQuestionnaireResponse;
          }
          provenanceTargetObservationIdsForDiff = provenanceView.provenanceTargetObservationIds;
          setProvenanceTargetObservationIds(provenanceView.provenanceTargetObservationIds);
          setProvenanceTargetFlowsheetQuestionnaireResponseIds(
            provenanceView.provenanceTargetFlowsheetQuestionnaireResponseIds,
          );
          provenanceStageMembershipChanges = provenanceView.stageMembershipChanges;
          provenanceObservations = provenanceView.observations;

          if (provenanceView.flowsheetQuestionnaireResponses.length > 0) {
            const filteredById = new Map(resolvedQuestionnaireResponsesById);
            for (const flowsheetQuestionnaireResponse of provenanceView.flowsheetQuestionnaireResponses) {
              if (flowsheetQuestionnaireResponse.id) {
                filteredById.set(flowsheetQuestionnaireResponse.id, flowsheetQuestionnaireResponse);
              }
            }
            resolvedQuestionnaireResponsesById = filteredById;
          }
        }

        const [stageObservations, stageObservationsForDiff] = await Promise.all([
          fetchLaborStageObservations(medplum, clientId, encounterId),
          fetchLaborStageObservationsWithMembersForVersionDiff(medplum, clientId, encounterId),
        ]);

        const mergeObservations = (observations: Observation[]) => {
          const merged = [...observations];
          const seenIds = new Set(
            merged.map((observation) => observation.id).filter(Boolean) as string[],
          );
          for (const observation of provenanceObservations) {
            if (observation.id && !seenIds.has(observation.id)) {
              seenIds.add(observation.id);
              merged.push(observation);
            }
          }
          return merged;
        };

        const mergedStageObservations = mergeObservations(stageObservations);
        const mergedStageObservationsForDiff = mergeObservations(stageObservationsForDiff);
        const resolvedStageMembershipChanges =
          await resolveLaborStageMembershipChangesForVersionDiff({
            medplum,
            laborStageObservationsForDiff: mergedStageObservationsForDiff,
            previousEncounterQuestionnaireResponse: resolvedPreviousVersion,
            currentEncounterQuestionnaireResponse: resolvedCurrentVersion,
            provenanceStageMembershipChanges,
            provenanceTargetObservationIds: provenanceTargetObservationIdsForDiff,
          });

        setLaborStageObservations(mergedStageObservations);
        setLaborStageObservationsForDiff(mergedStageObservationsForDiff);
        setQuestionnaireResponsesById(resolvedQuestionnaireResponsesById);
        setStageMembershipChanges(resolvedStageMembershipChanges);
        setCurrentVersion(resolvedCurrentVersion);
        setPreviousVersion(resolvedPreviousVersion);
      } catch (error) {
        captureExceptionWithoutPhi("Failed to load the labor encounter version diff", error, {
          encounterId,
          versionId,
        });
        toast({
          title: "Failed to load this labor amendment",
          message:
            "The records this amendment changed could not be read. Reload the page to try again, or contact support if the error persists.",
        });
      } finally {
        setIsFetchingInitialData(false);
      }
    };
    void getVersionHistory();
  }, [versionId, encounterId, clientId, medplum]);

  if (isFetchingInitialData || !encounter) return <Loading />;

  if (!isFetchingInitialData && versionId && !currentVersion) {
    return <NotFound />;
  }

  const hasEncounterAmendmentWindow = Boolean(currentVersion && previousVersion);
  const showOnlyObservationVersionChainDiffs =
    Boolean(currentVersion) && !hasEncounterAmendmentWindow;
  const hasLaborData = laborStageObservations.length > 0;
  const hasStageMembershipChanges = stageMembershipChanges.length > 0;

  const lastAmendedBy = currentVersion?.meta?.author?.display;
  const lastAmended = currentVersion?.meta?.lastUpdated;

  const orderedLaborStages = [
    LOINCAnswerCodes.SecondStageLabor,
    LOINCAnswerCodes.ActiveLabor,
    LOINCAnswerCodes.EarlyLabor,
    LOINCAnswerCodes.ProdromalLabor,
  ];

  const stagesWithObservations = orderedLaborStages
    .map((stageCode) => {
      const stageObservations = laborStageObservations.filter(
        (observation) => observation.valueCodeableConcept?.coding?.[0]?.code === stageCode,
      );
      return {
        stageCode,
        stageDisplay: stageObservations[0]?.valueCodeableConcept?.coding?.[0]?.display || stageCode,
        observations: stageObservations,
      };
    })
    .filter(
      (stage) =>
        stage.observations.length > 0 || stageCodesWithMembershipChanges.has(stage.stageCode),
    );

  const exitAmendmentView = () => {
    navigate(`/clients/${clientId}/encounters/${encounterId}`, {
      preventScrollReset: true,
    });
  };

  return (
    <Stack py="md">
      <LaborVersionDiffHeader
        questionnaireResponse={currentVersion}
        observation={laborStageObservations[0]}
        onExitAmendmentView={exitAmendmentView}
      />

      {showOnlyObservationVersionChainDiffs && (
        <Alert color="yellow" title="Previous encounter details unavailable">
          Only one saved version exists for this amendment, so encounter-level fields cannot be
          compared to Sign & Lock. Flowsheet changes below are limited to rows that were updated
          during this amendment.
        </Alert>
      )}

      {hasEncounterAmendmentWindow && (
        <>
          <EncounterDifference currentVersion={currentVersion} previousVersion={previousVersion} />
          <DxDiagnosesVersionDiff
            currentVersion={currentVersion}
            previousVersion={previousVersion}
          />
          {encounter && <ProceduresAmendmentDiff encounter={encounter} versionId={versionId} />}
          <EncounterNoteDifference
            currentVersion={currentVersion}
            previousVersion={previousVersion}
          />
          <EncounterSummaryDifference
            currentVersion={currentVersion}
            previousVersion={previousVersion}
          />
        </>
      )}

      {(hasEncounterAmendmentWindow ||
        showOnlyObservationVersionChainDiffs ||
        hasStageMembershipChanges) &&
        (hasLaborData || hasStageMembershipChanges) &&
        stagesWithObservations.map((stage) => (
          <LaborStageDiff
            key={stage.stageCode}
            stageCode={stage.stageCode}
            stageDisplay={stage.stageDisplay}
            stageMembershipChanges={stageMembershipChanges}
            currentEncounterQuestionnaireResponse={currentVersion}
            questionnaireResponsesById={questionnaireResponsesById}
            provenanceObservationsById={provenanceObservationsById}
            previousEncounterQuestionnaireResponse={previousVersion}
            laborStageObservationsForDiff={laborStageObservationsForDiff}
            provenanceTargetObservationIds={provenanceTargetObservationIds}
            restrictToEncounterAmendmentWindow={hasEncounterAmendmentWindow}
            showOnlyObservationVersionChainDiffs={showOnlyObservationVersionChainDiffs}
            provenanceTargetFlowsheetQuestionnaireResponseIds={
              provenanceTargetFlowsheetQuestionnaireResponseIds
            }
          />
        ))}

      {(hasEncounterAmendmentWindow || showOnlyObservationVersionChainDiffs) && (
        <PostpartumDiff
          encounter={encounter}
          currentEncounterQuestionnaireResponse={currentVersion}
          questionnaireResponsesById={questionnaireResponsesById}
          previousEncounterQuestionnaireResponse={previousVersion}
          restrictToEncounterAmendmentWindow={hasEncounterAmendmentWindow}
          showOnlyObservationVersionChainDiffs={showOnlyObservationVersionChainDiffs}
        />
      )}

      <FilesDiff
        currentEncounterQuestionnaireResponse={currentVersion}
        previousEncounterQuestionnaireResponse={previousVersion}
      />

      <Group justify="flex-end">
        <Stack align="flex-end">
          <Button variant="outline" onClick={exitAmendmentView}>
            Exit Amendment View
          </Button>
          {lastAmendedBy && lastAmended && (
            <TextWrapper>
              Last amended by {lastAmendedBy} - {format(lastAmended, "MM/dd/yyyy")}
            </TextWrapper>
          )}
        </Stack>
      </Group>
    </Stack>
  );
};

export default LaborVersionDiff;
